import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import TouchButton from '~/common/TouchButton';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 59,
    padding: theme.spacing(3, 4),
    maxWidth: 750,
    width: '100%',
    margin: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    fontSize: 24,
    fontWeight: 500,
  },
  backButton: {
    position: 'absolute',
    left: theme.spacing(1),
  },
  body: {
    textAlign: 'center',
    marginBottom: theme.spacing(1),
    '& p': { margin: theme.spacing(1, 0) },
    '& ul': {
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(3),
      '& li': { marginBottom: theme.spacing(0.5) },
    },
  },
  textField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 6,
      borderColor: '#375043',
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '&& > *:not(:first-child)': {
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
  },
  buttonPrimary: {
    minHeight: 65,
    width: '55%',
    height: 'fit-content',
    fontSize: '1rem',
    background: '#dde8b7',
    border: '2px solid #375043',
    textTransform: 'none',
    borderRadius: '12px',
    '& .MuiButton-label': {
      fontWeight: 900,
      fontSize: '1rem',
      lineHeight: 1.2,
      fontFamily: theme.typography.fontFamily,
    },
  },
  buttonSecondary: {
    minHeight: 65,
    marginTop: theme.spacing(1),
    width: '55%',
    fontSize: '1rem',
    background: '#f2f6f9',
    border: '2px solid #375043',
    textTransform: 'none',
    borderRadius: '12px',
    '& .MuiButton-label': {
      fontWeight: 900,
      fontSize: '1rem',
      lineHeight: 1.2,
      fontFamily: theme.typography.fontFamily,
    },
  },
}));

export default function Modal({
  open,
  onClose,
  showBackButton = false,
  onBack,
  title,
  children,
  textareaProps,
  actions,
  maxWidth = 'xs',
  paperStyle,           
  contentStyle,       
  actionsStyle,          
  blockOutsideClose, 
}) {
  const classes = useStyles();

  const handleDialogClose = (event, reason) => {
    if (blockOutsideClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (onClose) onClose(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth={maxWidth}
      classes={{ paper: classes.dialogPaper }}
      PaperProps={paperStyle ? { style: paperStyle } : undefined}
    >
      <DialogTitle disableTypography>
        <div className={classes.header}>
          {showBackButton && (
            <IconButton
              className={classes.backButton}
              onClick={onBack}
              aria-label="Go back"
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {title}
        </div>
      </DialogTitle>

      <DialogContent style={contentStyle}>
        <div className={classes.body}>{children}</div>
        {textareaProps && (
          <TextField
            className={classes.textField}
            placeholder={textareaProps.placeholder}
            value={textareaProps.value}
            onChange={textareaProps.onChange}
            onPaste={textareaProps.onPaste}
            rows={textareaProps.rows || 4}
            multiline
            variant="outlined"
            fullWidth
            helperText={textareaProps.helperText}
            FormHelperTextProps={textareaProps.FormHelperTextProps}
            error={Boolean(textareaProps && textareaProps.error)}
          />
        )}
      </DialogContent>

      <DialogActions className={classes.actions} style={actionsStyle}>
        {actions.map((action, idx) => {
          const {
            label,
            onClick,
            variant = 'primary',
            disabled,
            type,
            width,
            style,
          } = action;

          const full = typeof width === 'undefined';
          const styleObj =
            typeof width === 'undefined'
              ? style
              : { width: typeof width === 'number' ? `${width}px` : width, ...(style || {}) };

          return (
            <TouchButton
              key={idx}
              fullWidth={full}
              type={type || 'button'}
              onClick={onClick}
              disabled={disabled}
              className={variant === 'primary' ? classes.buttonPrimary : classes.buttonSecondary}
              style={styleObj}
            >
              {label}
            </TouchButton>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showBackButton: PropTypes.bool,
  onBack: PropTypes.func,
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  textareaProps: PropTypes.shape({
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onPaste: PropTypes.func,
    rows: PropTypes.number,
    helperText: PropTypes.node,
    FormHelperTextProps: PropTypes.object,
    error: PropTypes.bool,
  }),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['primary', 'secondary']),
      disabled: PropTypes.bool,
      type: PropTypes.oneOf(['button', 'submit']),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      style: PropTypes.object,
    })
  ).isRequired,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md']),
  paperStyle: PropTypes.object,
  contentStyle: PropTypes.object,
  actionsStyle: PropTypes.object,
  blockOutsideClose: PropTypes.bool,
};
